import { SelectField } from '@atlas/ui-components'
import { type ReactNode, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useIgnoreWorkfrontError, useRetryWorkfrontError } from '@/hooks/useWorkfront'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { WorkfrontErrorDTO } from '@/services/workfront.service'
import type { ErrorActions } from './components/ErrorsTable'
import { humanize } from './components/StatusBadges'
import { IgnoreErrorDialog } from './components/WorkfrontDialogs'

/** Where everything in Admin › Workfront lives. */
export const WORKFRONT_PATHS = {
	overview: '/admin/workfront',
	bundles: '/admin/workfront/bundles',
	bundle: (id: string) => `/admin/workfront/bundles/${id}`,
	mappings: '/admin/workfront/mappings',
	pmRules: '/admin/workfront/pm-rules',
	runs: '/admin/workfront/runs',
	run: (id: string) => `/admin/workfront/runs/${id}`,
	streams: '/admin/workfront/streams',
	errors: '/admin/workfront/errors',
	error: (id: string) => `/admin/workfront/errors/${id}`,
	settings: '/admin/workfront/settings',
}

/**
 * A bundle's name as the list and its page show it: its Workfront title, else "Not fetched
 * yet" when it was never fetched (its title is not known), else its program ID.
 */
export function bundleTitleOf(bundle: {
	title: string | null
	lastFetchedAt: string | null
	programId: string
}): string {
	const title = bundle.title?.trim()
	if (title) return title
	return bundle.lastFetchedAt ? `Program ${bundle.programId}` : 'Not fetched yet'
}

/** Results per page, the API's own default. */
export const PAGE_SIZE = 25

const SECTIONS = [
	{ to: WORKFRONT_PATHS.overview, label: 'Overview', end: true },
	{ to: WORKFRONT_PATHS.bundles, label: 'Bundles', end: false },
	{ to: WORKFRONT_PATHS.mappings, label: 'Mappings', end: false },
	{ to: WORKFRONT_PATHS.pmRules, label: 'PM rules', end: false },
	{ to: WORKFRONT_PATHS.runs, label: 'Runs', end: false },
	{ to: WORKFRONT_PATHS.streams, label: 'Streams', end: false },
	{ to: WORKFRONT_PATHS.errors, label: 'Errors', end: false },
	{ to: WORKFRONT_PATHS.settings, label: 'Settings', end: false },
]

const sectionClass = ({ isActive }: { isActive: boolean }) =>
	`rounded-ds-sm px-3 py-1.5 text-ds-sm ${isActive ? 'bg-ds-surface font-medium' : 'text-ds-text-muted hover:text-ds-text'}`

/** The area's heading and its sections, on every Workfront page. */
export function WorkfrontHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<p className="text-ds-sm text-ds-text-muted">Admin › Workfront</p>
					<h1 className="font-semibold text-2xl">{title}</h1>
				</div>
				{actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
			</div>
			<nav aria-label="Workfront sections" className="flex gap-1">
				{SECTIONS.map((section) => (
					<NavLink key={section.to} to={section.to} end={section.end} className={sectionClass}>
						{section.label}
					</NavLink>
				))}
			</nav>
		</div>
	)
}

/** A filter over one enum: every value, or one. */
export function EnumFilter<T extends string>({
	label,
	values,
	value,
	onChange,
}: {
	label: string
	values: readonly T[]
	value: T | undefined
	onChange: (value: T | undefined) => void
}) {
	return (
		<SelectField
			label={label}
			value={value ?? ''}
			placeholder="any"
			options={values.map((option) => ({ value: option, label: humanize(option) }))}
			onChange={(next) => onChange(next === '' ? undefined : (next as T))}
		/>
	)
}

/**
 * Retry and Ignore for errors, wherever they are listed: both optimistic, both
 * reported through the status bar. Ignoring opens the dialog that asks why.
 */
export function useErrorActions(): { actions: ErrorActions; dialog: ReactNode } {
	const retry = useRetryWorkfrontError()
	const ignore = useIgnoreWorkfrontError()
	const [ignoring, setIgnoring] = useState<WorkfrontErrorDTO | null>(null)

	const actions: ErrorActions = {
		onRetry: (error) =>
			retry.mutate(error.id, {
				onSuccess: () => notification.success(`Retrying: ${error.title}`),
				onError: (cause) => notification.error(apiErrorMessage(cause, 'Could not retry that problem.')),
			}),
		onIgnore: (error) => setIgnoring(error),
	}
	const dialog = (
		<IgnoreErrorDialog
			open={ignoring !== null}
			title={ignoring?.title ?? ''}
			submitting={ignore.isPending}
			onCancel={() => setIgnoring(null)}
			onIgnore={(reason) => {
				const error = ignoring
				if (!error) return
				setIgnoring(null)
				ignore.mutate(
					{ id: error.id, reason },
					{
						onSuccess: () => notification.success(`Ignored: ${error.title}`),
						onError: (cause) => notification.error(apiErrorMessage(cause, 'Could not ignore that problem.')),
					},
				)
			}}
		/>
	)
	return { actions, dialog }
}
